import React, { useEffect, useState } from 'react';
import api from '../../api';
import { useAuthContext } from '../../hooks/UseAuthContext';
import { FaUser, FaChevronDown, FaTrash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import ConfirmationBox from '../../Components/Confirmation/ConfirmationBox';
import './consistentcharacter.css';

function CharacterSelector({ selectedCharacterId, onCharacterSelect }) {
  const { user } = useAuthContext();
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [characterToDelete, setCharacterToDelete] = useState(null);

  useEffect(() => {
    loadCharacters();
  }, []);

  const loadCharacters = async () => {
    setLoading(true);
    try {
      const res = await api.get('/characters', {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setCharacters(res.data.characters || []);
    } catch (error) {
      console.error('Failed to load characters:', error);
      toast.error('Failed to load characters');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (e, characterId, characterName) => {
    e.stopPropagation(); // Prevent dropdown from closing
    setCharacterToDelete({ id: characterId, name: characterName });
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async (confirmed) => {
    setShowDeleteConfirm(false);
    
    if (!confirmed || !characterToDelete) {
      setCharacterToDelete(null);
      return;
    }

    const { id: characterId, name: characterName } = characterToDelete;
    
    setDeletingId(characterId);
    try {
      await api.delete(`/characters/${characterId}`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      
      toast.success(`${characterName} deleted successfully`);
      
      // Remove from local state
      setCharacters(prev => prev.filter(c => c._id !== characterId));
      
      // If deleted character was selected, clear selection
      if (selectedCharacterId === characterId) {
        onCharacterSelect(null);
      }
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.error || 'Failed to delete character');
    } finally {
      setDeletingId(null);
      setCharacterToDelete(null);
    }
  };

  const selectedChar = characters.find(c => c._id === selectedCharacterId);

  return (
    <>
      <div className="character-selector">
        <div 
          className="character-selector-trigger"
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="selected-character-preview">
            {selectedChar ? (
              <>
                <img 
                  src={`${process.env.REACT_APP_MEDIA_URL}`+selectedChar.image_url} 
                  alt={selectedChar.name}
                  className="character-thumb"
                />
                <div className="character-info">
                  <span className="character-name">{selectedChar.name}</span>
                  <span className="character-meta">
                    {selectedChar.style_lock?.substring(0, 30)}...
                  </span>
                </div>
              </>
            ) : (
              <>
                <FaUser className="placeholder-icon" />
                <span className="placeholder-text">Select a character...</span>
              </>
            )}
          </div>
          <FaChevronDown className={`dropdown-icon ${isOpen ? 'open' : ''}`} />
        </div>

        {isOpen && (
          <div className="character-dropdown">
            {loading ? (
              <div className="loading-state">Loading characters...</div>
            ) : characters.length === 0 ? (
              <div className="empty-state">
                No characters yet. Create one to get started.
              </div>
            ) : (
              <div className="character-list">
                {characters.map((char) => (
                  <div
                    key={char._id}
                    className={`character-option ${selectedCharacterId === char._id ? 'selected' : ''}`}
                    onClick={() => {
                      onCharacterSelect(char._id);
                      setIsOpen(false);
                    }}
                  >
                    <img 
                      src={`${process.env.REACT_APP_MEDIA_URL}`+char.image_url} 
                      alt={char.name}
                      className="character-thumb"
                    />
                    <div className="character-details">
                      <span className="character-name">{char.name}</span>
                      <span className="character-style">
                        {char.style_lock?.substring(0, 40)}...
                      </span>
                    </div>
                    
                    {/* Delete Button */}
                    <button
                      className="character-delete-btn"
                      onClick={(e) => handleDeleteClick(e, char._id, char.name)}
                      disabled={deletingId === char._id}
                      title="Delete character"
                    >
                      {deletingId === char._id ? (
                        < 
                          span className="deleting-spinner">⏳</span>
                      ) : (
                        <FaTrash style={{zIndex:99}} />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && characterToDelete && (
        <ConfirmationBox
          message={`Delete "${characterToDelete.name}"? This action cannot be undone.`}
          handleConfirm={handleDeleteConfirm}
        />
      )}
    </>
  );
}

export default CharacterSelector;